import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { issueGithubUrl, issueLabel, type RepoIssueRow } from '../lib/issues';
import { orgHref } from '../lib/org';
import { absolute, ago } from '../lib/time';
import { UNSET } from '../lib/unset';
import { IssueHandoffPill } from './IssueHandoffPill';
import { StatusPill } from './StatusPill';

// What the agent decided about one issue, as facts (arc 101 slice 06).
// Presenter — the drawer owns the fetch. Same projection as the table row, so
// the same two exits are offered: `#<n>` out to GitHub where the prose lives,
// `last_session_id` in to the session that decided it.

export function IssueTriageFacts(props: { issue: RepoIssueRow; encoded: string }): JSX.Element {
  const placeholder = (): JSX.Element => <span class="repo-issues__none">{UNSET}</span>;

  return (
    <>
      <dl class="facts">
        <div>
          <dt>{t('repo_detail.issues_col_state')}</dt>
          <dd>
            <StatusPill tone={props.issue.state === 'open' ? 'success' : 'neutral'}>
              {issueLabel('issues_state', props.issue.state)}
            </StatusPill>
          </dd>
        </div>
        <div>
          <dt>{t('repo_detail.issues_col_qualification')}</dt>
          <dd>{issueLabel('issues_qual', props.issue.qualification)}</dd>
        </div>
        <div>
          <dt>{t('repo_detail.issues_col_handoff')}</dt>
          <dd>
            <IssueHandoffPill status={props.issue.handoff_status} />
          </dd>
        </div>
        <div>
          <dt>{t('repo_detail.issues_col_last_action')}</dt>
          <dd>
            <Show when={props.issue.last_agent_action_at} fallback={placeholder()}>
              {(when) => (
                <time datetime={when()} title={absolute(when())}>
                  {ago(when())}
                </time>
              )}
            </Show>
          </dd>
        </div>
      </dl>

      <p class="issue-links">
        <a
          class="repo-issues__gh"
          href={issueGithubUrl(props.encoded, props.issue.github_number)}
          target="_blank"
          rel="noreferrer"
        >
          {t('repo_detail.issue_open_github')}
        </a>
        <Show when={props.issue.last_session_id}>
          {(sessionId) => (
            <A class="issue-action__link" href={orgHref(`/audit/${sessionId()}`)}>
              {t('repo_detail.issues_view_session')}
            </A>
          )}
        </Show>
      </p>
    </>
  );
}
